import {Injectable, inject} from '@angular/core'
import {Observable} from 'rxjs'
import {environment} from '@environments/environment'
import {EnviromentIds} from '@common/global-constants'
import {EnviromentService} from '@service/env.service'
import {PlatformService} from '@service/platform.service'
import {TranslationServicex} from '@service/translationx.service'
import {ChatContract} from '@uve-solutions/f-chat/core'
import {
  type ChatConfig,
  type ChatEnvironment,
  type ChatOrigin,
  Platform as ChatPlatform,
  Conversation,
  type Product,
  TemplateMessage,
  type User,
  UserAccessType,
} from '@uve-solutions/f-chat/models'
import {ChatApiAdapter} from '../adapters/chat-api.adapter'
import {ChatConversationService} from './chat-conversation.service'
import type {Outlet} from '@models/outlet'

@Injectable({
  providedIn: 'root',
})
export class ChatFacade extends ChatContract {
  apiService = inject<ChatApiAdapter>(ChatApiAdapter)
  private readonly translationService = inject(TranslationServicex)
  private readonly chatConversationService = inject(ChatConversationService)
  private readonly platformService = inject(PlatformService)
  private readonly envService = inject(EnviromentService)

  chatConfig: ChatConfig = {
    language: this.translationService.getActiveLanguage(),
    platform: this.getChatPlatform(),
  }

  reactivity = {
    socketIoUrl: environment.chatappUrl,
    manualConnection: true,
  }

  environment: ChatEnvironment = {
    emailIntegratorBaseUrl: this.envService.getEnvironmentData(EnviromentIds.EMAIL_INTEGRATOR_SERVICE_URL),
  }

  getCurrentUser = (): User => {
    const user = this.apiService.getUser()
    return {
      organizationId: user?.organizationId?.toString() || '',
      userName: user?.userName,
      type: user?.type,
      userAccessType: UserAccessType.Member,
      isImpersonate: user?.isImpersonate ?? false,
      originalUser: user?.impersonateUsername || '',
      id: user?.userId,
    } as User
  }

  getConversation = (): Conversation => {
    const user = this.apiService.getUser()
    const outletId = this.chatConversationService.getCurrentOutletId()
    const conversationId = this.chatConversationService.getConversationIdByOutletId(outletId)
    const conversation = this.chatConversationService.getConversationById(conversationId)?.()

    const outlet = conversation?.metadata?.outlets?.find(o => o.customerId.toString() === outletId)
    const conversationTo = conversation?.users?.find(u => u.userId !== user?.userId)

    return {
      channelId: conversation?.id ?? '',
      userId: conversationTo?.userId ?? -1,
      firstname: user?.name ?? '',
      customerId: outlet?.customerId?.toString() ?? '',
      customerCode: outlet?.customerCode ?? '',
    }
  }

  translate = (value: string, replacements?: {[key: string]: string} | null): string => {
    if (!value) {
      return ''
    }
    return this.translationService.instant(value, replacements)
  }

  getCatalogProducts = (searchTerm: string, limit: number, offset: number): Observable<Product[]> => {
    const conversation = this.chatConversationService.getCurrentConversation()?.()
    const outletMetadata =
      conversation?.metadata?.outlets?.find(o => o.customerId?.toString() === conversation.metadata.selectedOutlet) ??
      conversation?.metadata?.outlets?.[0]
    const outlet: Outlet = {id: outletMetadata?.customerId} as Outlet

    return this.chatConversationService.getProducts(searchTerm, limit, offset, outlet)
  }

  setConversationStatus = (origin: ChatOrigin | null = null): void => {
    if (!origin) {
      return
    }
    this.chatConversationService.updateStatusConversation(origin)
  }

  getParamTemplates = (): TemplateMessage[] => this.chatConversationService.getTemplates()

  private getChatPlatform(): ChatPlatform {
    if (this.platformService.isAndroid()) {
      return ChatPlatform.Android
    }
    if (this.platformService.isIOS()) {
      return ChatPlatform.iOS
    }
    return ChatPlatform.Web
  }
}
